import bindAllOfThis from "src/utils/BindThisHelper";
import initializeInteractiveCursor from "src/games/shared/InteractiveCursorUtil";
import { TiledGameObject } from "../../shared/phaserHelpers/HandyTypes";
import { PhaserButtonConfig } from "../../shared/Button";
import { Color } from "src/games/shared/ColorValue";
import TranslateApi from "src/utils/TranslateHelper";
import { PuzzlePiece } from "../objects/PuzzlePiece";
import { Slot } from "../objects/Slot";
import { EndGameScene } from "src/games/JigsawPuzzleGame/scenes/EndGameScene";
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    finalPicture: Phaser.GameObjects.Image;
    targetArea: Phaser.Geom.Rectangle;
    slots: Slot[][];
    pieces: PuzzlePiece[][];
    piecesInfo;

    private showSlotLines: boolean = false;

    private NUMBER_OF_PIECES;
    private SLOT_SIZE;
    private GRID_SIZE;

    private translateString: string = "JigsawPuzzle.Phaser.";

    constructor() {
        super({
            key: "MainScene"
        });
    }

    /**
     * NOTE: It is ESSENTIAL for the pieces to be named correctly in the atlas file!
     * Each piece should be named "piece-<correct row>-<correct column>" 
     * e.g. "piece-0-0" corresponds to the piece that should be placed in the top left corner of the puzzle
     */
    preload(): void {
        this.load.image("background", require("/src/assets/qci/backgrounds/planet_s_top.jpg"));
        // Uncomment for 5x5 puzzle
        this.load.atlas("assets", require("/src/assets/qci/atlas/gameJigsawPuzzle5x5.png"), require("/src/assets/qci/atlas/gameJigsawPuzzle5x5.json"));
        this.load.image("final-picture", require("/src/assets/qci/sprites/2s_puzzle.png"));
        this.load.tilemapTiledJSON("map", require("/src/assets/qci/maps/jigsaw_puzzle_game.json"));
    }

    initialize(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'map' });
        this.finalPicture = this.add.image(0, 0, "final-picture");
        this.finalPicture.setAlpha(0);
        // Count the number of frames in the atlas file to get the number of pieces
        this.NUMBER_OF_PIECES = this.textures.get("assets").getFrameNames().length;
        this.GRID_SIZE = Math.sqrt(this.NUMBER_OF_PIECES);
        this.SLOT_SIZE = this.finalPicture.width / this.GRID_SIZE;

        this.pieces = [];
        for (var row = 0; row < this.GRID_SIZE; row++) {
            this.pieces[row] = [];
        }

        this.slots = [];
    }

    registerEvents() {
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, piece: PuzzlePiece, dragX: number, dragY: number) => {
            piece.x = pointer.downX;
            piece.y = pointer.downY;
            if (this.tweens.isTweening(piece)) {
                this.tweens.killAll();
            }
            this.children.bringToTop(piece);

        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, piece: PuzzlePiece, dragX: number, dragY: number) => {
            piece.x = pointer.x;
            piece.y = pointer.y;
        });

        this.input.on('drop', (pointer: Phaser.Input.Pointer, piece: PuzzlePiece, slot: Slot) => {
            this.children.moveDown(piece);
            this.acceptPieceInSlot(piece, slot);
        });
    }


    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.initialize();
        this.generateScene();
        this.matchPiecesWithSlots();
        initializeInteractiveCursor(this);
        this.registerEvents();
    }

    generateScene() {
        var background = this.add.image(0, 0, "background");
        background.setOrigin(0);
        background.setDisplaySize(this.gameWidth, this.gameHeight);

        var legend;

        this.piecesInfo = require("../objects/puzzle5x5.json");

        this.map.getObjectLayer("Game").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "tray") {
                var id = 0;
                for (var frameName of this.textures.get("assets").getFrameNames()) {
                    var piece = this.createPiece(id, frameName, ob);
                    id++;
                    this.pieces[piece.correctPosition.x][piece.correctPosition.y] = piece;
                }
            }
            else if (ob.name === "target") {
                this.targetArea = new Phaser.Geom.Rectangle(ob.x, ob.y, this.finalPicture.width, this.finalPicture.height);
                this.add.graphics().lineStyle(4, 0xffffff).strokeRectShape(this.targetArea);

                var x, y;
                id = 0;
                for (var row = 0; row < this.GRID_SIZE; row++) {
                    y = ob.y + (row * this.SLOT_SIZE);
                    this.slots[row] = [];
                    for (var col = 0; col < this.GRID_SIZE; col++) {
                        x = ob.x + (col * this.SLOT_SIZE);
                        var slot = this.createSlot(id, x, y, row, col);
                        this.slots[row][col] = slot;
                        id++;
                    }
                }
            }
            else if (ob.name === "legend") {
                legend = this.add.image(ob.x, ob.y, "final-picture");
                legend.setOrigin(0);
                legend.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "pilot") {
                var pilot = this.add.image(ob.x, ob.y, "pilots", "s");
                pilot.setOrigin(0);
                pilot.setDisplaySize(ob.width, ob.height);
            }
        });
    }

    acceptPieceInSlot(piece: PuzzlePiece, slot: Slot): void {
        if (this.isCorrectMatch(piece, slot)) {
            this.lockPiece(piece, slot);
        }
    }

    /**
     * Animation of shaking the piece cause it's wrong
     */
    shakePiece(piece) {
        if (this.tweens.isTweening(piece)) {
            return;
        }
        // Little shake effect
        this.tweens.add({
            targets: piece,
            duration: 100,
            repeat: 5,
            ease: "Linear",
            x: "+=8"
        });
    }

    /**
     * It's the correct piece, lock it into it's rightful slot
     */
    lockPiece(piece: PuzzlePiece, slot: Slot) {
        slot.filled = true;
        piece.enabled = false;
        piece.disableInteractive();

        this.setX(piece, slot);
        this.setY(piece, slot);

        // Check if the game is over
        this.isGameOver();
    }

    setY(piece: PuzzlePiece, slot: Slot) {
        var knobs = this.piecesInfo[slot.position.x][slot.position.y];

        var offsetY = piece.height - slot.height;

        // Only knob on the top, center with offset a little to the top
        if (knobs.top === "out" && knobs.bottom !== "out") {
            piece.y = slot.y - offsetY / 2;
        }
        // Only knob on the bottom, center with offset a little to the bottom
        else if (knobs.top !== "out" && knobs.bottom === "out") {
            piece.y = slot.y + offsetY / 2;
        }
        else {
            // Either two knobs out, or no knobs out, centered already
            piece.y = slot.y;
        }
    }

    setX(piece, slot) {
        var knobs = this.piecesInfo[slot.position.x][slot.position.y];

        var offsetX = piece.width - slot.width;

        // Only knob out on the right, center with offset a little to the right
        if (knobs.right === "out" && knobs.left !== "out") {
            piece.x = slot.x + offsetX / 2;
        }
        // Only knob on the left, center with offset a little to the left
        else if (knobs.left === "out" && knobs.right !== "out") {
            piece.x = slot.x - offsetX / 2;
        }
        else {
            // Either two knobs out, or no knobs out
            piece.x = slot.x;
        }
    }

    isCorrectMatch(piece: PuzzlePiece, slot: Slot) {
        return (piece.correctPosition.x === slot.position.x && piece.correctPosition.y === slot.position.y);
    }

    createSlot(id, x, y, row, col): Slot {
        var position = new Phaser.Math.Vector2(row, col);
        var slot = new Slot(this, x, y, this.SLOT_SIZE, this.SLOT_SIZE, id, position);

        // Debug - draw lines
        if (this.showSlotLines) {
            this.add.graphics().lineStyle(2, 0xffffff).strokeRectShape(slot.getBounds());
        }

        return slot;
    }

    createPiece(id: number, frameName: string, tray: TiledGameObject): PuzzlePiece {
        var padding = 50;
        // Parse the given pieces' frame name to get it's correct slot position
        var name = frameName.split("-");
        var row = parseInt(name[1]) as number;
        var col = parseInt(name[2]) as number;
        var correctPosition = new Phaser.Math.Vector2(row, col);
        var currentPosition = new Phaser.Math.Vector2(-1, -1);

        var piece = new PuzzlePiece(this, 0, 0, frameName, id, currentPosition, correctPosition);
        piece.setRandomPosition(tray.x, tray.y, tray.width - padding, tray.height - padding);

        return piece;
    }

    getCorrectSlot(piece: PuzzlePiece): Slot {
        return this.slots[piece.correctPosition.x][piece.correctPosition.y];
    }

    /**
     * Links up the pieces and slots correctly
     */
    matchPiecesWithSlots() {
        for (var row = 0; row < this.GRID_SIZE; row++) {
            for (var col = 0; col < this.GRID_SIZE; col++) {
                var piece = this.pieces[row][col];
                piece.correctSlotId = this.getCorrectSlot(piece).id;
            }
        }
    }

    modal(translationKey: string) {
        var text = TranslateApi.translate(this.translateString + translationKey);
        var buttonConfig: PhaserButtonConfig = {
            smartSize: true,
            backgroundColor: Color.KUDER_SECONDARY_BUTTON
        };

        var config: PhaserModalConfig = {
            title: text,
            buttonWidth: 200,
            buttonConfig: buttonConfig
        };

        var buttonText = TranslateApi.translate("common.ok");

        var options = [{ id: 1, displayText: buttonText }];
        var modal = new PhaserModal(this, 0, 0, options, config);
        modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
        modal.on("optionClicked", () => {
            modal.destroy();
        });
    }

    isGameOver(): void {
        for (var row = 0; row < this.GRID_SIZE; row++) {
            for (var col = 0; col < this.GRID_SIZE; col++) {
                var slot = this.slots[row][col];
                if (!slot.filled) {
                    return;
                }
            }
        }

        this.showFinalPictureTween();
    }

    showFinalPictureTween() {
        this.finalPicture.setOrigin(0);
        this.finalPicture.setPosition(this.targetArea.x, this.targetArea.y);
        this.finalPicture.setDepth(2);

        this.tweens.add({
            targets: this.finalPicture,
            ease: "Power1",
            duration: 1500,
            alpha: {
                getStart: () => 0,
                getEnd: () => 1,
            },
            onComplete: () => {
                // Go to end game scene
                this.scene.add("EndGameScene", EndGameScene, true);
                this.scene.remove(this);
            }
        });
    }

}



// WEBPACK FOOTER //
// ./src/games/JigsawPuzzleGame/scenes/MainScene.ts